import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";

interface CustomSignInFormProps {
  onSwitchToSignUp: () => void;
  onRecoveryClick: () => void;
}

export const CustomSignInForm = ({ onSwitchToSignUp, onRecoveryClick }: CustomSignInFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { error: showError } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        showError("Credenciales incorrectas. Verifica tu email y contraseña.");
      }
    } catch (error) {
      console.error("Error during sign in:", error);
      showError("Ocurrió un error durante el inicio de sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2 animate-fade-in">
        <Label htmlFor="email" className="text-white font-medium">
          Correo electrónico
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            required
            className="bg-black/50 text-white border-white/20 placeholder:text-white/60 pl-10 transition-all duration-200 focus:border-white/40"
          />
        </div>
      </div>

      <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
        <Label htmlFor="password" className="text-white font-medium">
          Contraseña
        </Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Tu contraseña"
            required
            className="bg-black/50 text-white border-white/20 placeholder:text-white/60 pl-10 pr-10 transition-all duration-200 focus:border-white/40"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-[#00C1D6] hover:bg-[#2196A3] text-white font-semibold h-11 transition-all duration-200 hover:shadow-lg animate-fade-in"
        style={{ animationDelay: '0.2s' }}
      >
        {loading ? "Iniciando sesión..." : "Iniciar sesión"}
      </Button>

      <div className="flex items-center gap-4 py-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
        <div className="flex-1 h-px bg-white/20"></div>
        <span className="text-white/60 text-sm">o</span>
        <div className="flex-1 h-px bg-white/20"></div>
      </div>

      <div className="flex flex-col gap-2 animate-fade-in" style={{ animationDelay: '0.4s' }}>
        <button
          type="button"
          onClick={onRecoveryClick}
          className="text-white font-medium text-sm hover:text-white/80 transition-all duration-200 bg-white/10 px-4 py-2.5 rounded-lg hover:bg-white/20 w-full"
        >
          ¿Olvidaste tu contraseña?
        </button>
        
        <button
          type="button"
          onClick={onSwitchToSignUp}
          className="text-white font-medium text-sm hover:text-white/80 transition-all duration-200 bg-white/10 px-4 py-2.5 rounded-lg hover:bg-white/20 w-full"
        >
          ¿No tienes cuenta? Regístrate
        </button>
      </div>
    </form>
  );
};